import React, { useState, useEffect, useCallback } from 'react';
import {
  TransactionComp,
  AssetDossier,
  QueueDistressRecord,
  UserWorkspace,
  UserSavedScenario,
  UserAssetAnnotation,
  StudioSession,
  UnderwritingInputs,
} from './types';
import {
  checkSession,
  verifyGhostToken,
  fetchComps,
  fetchDossier,
  fetchRadar,
  fetchUserWorkspace,
  saveUserScenario,
  deleteUserScenario,
  saveUserAnnotation,
  getIcMemoDownloadUrl,
} from './lib/api';
import { generateInstitutionalIcMemoPdf } from './lib/pdfMemoGenerator';
import {
  peekGhostTokenFromUrl,
  clearGhostTokenFromUrl,
  setActiveGhostToken,
  getActiveGhostToken,
  isGhostAuthMessage,
  parseGhostSessionBody,
  ghostHandoffProfile,
  GHOST_ACTIVE_TOKEN_KEY,
} from './lib/ghostSession';
import { ghostOrigins, STUDIO_AUTH_REFRESH_EVENT } from './config/auth';
import { Header } from './components/common/Header';
import { MetricRibbon } from './components/common/MetricRibbon';
import { TransactionsLedger } from './components/comps/TransactionsLedger';
import { ArbitrageScreener } from './components/arbitrage/ArbitrageScreener';
import { DossierDrawer } from './components/dossier/DossierDrawer';
import { DistressedQueueRadar } from './components/radar/DistressedQueueRadar';
import { UnderwritingSandbox } from './components/sandbox/UnderwritingSandbox';
import { UserWorkspaceView } from './components/workspace/UserWorkspaceView';
import { IcMemoModal } from './components/workspace/IcMemoModal';
import { AuthModal } from './components/auth/AuthModal';
import { GhostAuthProbe } from './components/auth/GhostAuthProbe';
import { ViralShareModal } from './components/common/ViralShareModal';

export const App: React.FC = () => {
  const [session, setSession] = useState<StudioSession>({
    authenticated: false,
    user_id: 'guest-anonymous',
    email: null,
    name: 'Guest Visitor',
    tier: 'guest',
  });

  const [currentTab, setCurrentTab] = useState<'comps' | 'arbitrage' | 'radar' | 'sandbox' | 'workspace'>('comps');
  const [comps, setComps] = useState<TransactionComp[]>([]);
  const [totalVolume, setTotalVolume] = useState<number>(14_800_000_000);
  const [avgAcre, setAvgAcre] = useState<number | null>(764_000);
  const [avgMw, setAvgMw] = useState<number | null>(286_000);

  // Selected asset & slide-over drawer
  const [selectedCompId, setSelectedCompId] = useState<string>('AXP-US-OH-00154');
  const [isDossierOpen, setIsDossierOpen] = useState<boolean>(false);
  const [activeDossier, setActiveDossier] = useState<AssetDossier | null>(null);

  // IC Memo Modal
  const [isIcMemoModalOpen, setIsIcMemoModalOpen] = useState<boolean>(false);
  const [activeMemoScenario, setActiveMemoScenario] = useState<UserSavedScenario | null>(null);

  const [radarTargets, setRadarTargets] = useState<QueueDistressRecord[]>([]);
  const [workspace, setWorkspace] = useState<UserWorkspace | null>(null);
  const [annotation, setAnnotation] = useState<UserAssetAnnotation | null>(null);

  // Filter state for comps ledger
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedDealType, setSelectedDealType] = useState('');
  const [selectedMinMw, setSelectedMinMw] = useState('');

  // Sandbox inputs preset state
  const [sandboxPreset, setSandboxPreset] = useState<Partial<UnderwritingInputs>>({
    it_load_mw: 250,
    rent_rate_usd_kw_month: 185,
  });

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [authProbeKey, setAuthProbeKey] = useState(0);

  // 1. Initial Authentication & URL Ghost Token Check
  const resolveAuth = useCallback(async () => {
    // A. Check URL for incoming token (?ghost_token=... or #ghost_token=...)
    const urlToken = peekGhostTokenFromUrl();
    if (urlToken) {
      try {
        const authRes = await verifyGhostToken(urlToken);
        if (authRes.authenticated) {
          setActiveGhostToken(urlToken);
          setSession(authRes);
          clearGhostTokenFromUrl();
          return;
        }
      } catch (e) {
        console.error('Failed to verify URL Ghost token:', e);
      }
      clearGhostTokenFromUrl();
    }

    // B. Check active storage / cookie token
    const heldToken = getActiveGhostToken();
    if (heldToken) {
      try {
        const authRes = await verifyGhostToken(heldToken);
        if (authRes.authenticated) {
          setSession(authRes);
          return;
        }
      } catch {}
    }

    // C. Check server session endpoint
    try {
      const sess = await checkSession();
      setSession(sess);
    } catch {}
  }, []);

  useEffect(() => {
    resolveAuth();
  }, [resolveAuth]);

  // 2. Cross-Origin PostMessage Listener (GhostAuthProbe iframe or window.opener from Ghost Portal)
  useEffect(() => {
    const trusted = new Set(ghostOrigins());

    function handleMessage(event: MessageEvent) {
      if (!trusted.has(event.origin)) return;
      if (!isGhostAuthMessage(event.data)) return;

      const token = parseGhostSessionBody(event.data.token);
      if (!token) return;

      const profile = ghostHandoffProfile(event.data);

      void verifyGhostToken(token, profile).then((verified) => {
        if (verified.authenticated) {
          setActiveGhostToken(token);
          setSession({
            authenticated: true,
            user_id: verified.user_id,
            email: profile.email || verified.email,
            name: profile.name || verified.name,
            tier: verified.tier,
          });
        }
      });
    }

    function handleAuthRefresh() {
      setAuthProbeKey((k) => k + 1);
      resolveAuth();
    }

    function handleVisibilityOrFocus() {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        setAuthProbeKey((k) => k + 1);
        resolveAuth();
      }
    }

    function handleStorage(event: StorageEvent) {
      if (
        event.key === GHOST_ACTIVE_TOKEN_KEY ||
        event.key === 'ghost_member_token' ||
        event.key === 'aixenergy_member_jwt'
      ) {
        resolveAuth();
      }
    }

    window.addEventListener('message', handleMessage);
    window.addEventListener(STUDIO_AUTH_REFRESH_EVENT, handleAuthRefresh);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener(STUDIO_AUTH_REFRESH_EVENT, handleAuthRefresh);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, [resolveAuth]);

  // 3. Fetch Comps on mount or filter change
  useEffect(() => {
    const loadComps = async () => {
      try {
        const data = await fetchComps({
          search: search || undefined,
          region: selectedState || undefined,
          deal_type: selectedDealType || undefined,
          min_mw: selectedMinMw ? Number(selectedMinMw) : undefined,
        });
        setComps(data.results);
        setTotalVolume(data.total_volume_usd);
        setAvgAcre(data.avg_price_per_acre_usd);
        setAvgMw(data.avg_price_per_mw_usd);

        if (data.results.length > 0 && !data.results.find((c) => c.canonical_project_id === selectedCompId)) {
          setSelectedCompId(data.results[0].canonical_project_id);
        }
      } catch (err) {
        console.error('Failed to load comps:', err);
      }
    };
    loadComps();
  }, [search, selectedState, selectedDealType, selectedMinMw]);

  // 4. Load Radar Targets
  useEffect(() => {
    const loadRadar = async () => {
      try {
        const data = await fetchRadar();
        setRadarTargets(data.results);
      } catch (err) {
        console.error('Failed to load radar:', err);
      }
    };
    loadRadar();
  }, []);

  // 5. Fetch Active Dossier when selectedCompId changes
  useEffect(() => {
    if (!selectedCompId) return;
    const loadDossier = async () => {
      try {
        const d = await fetchDossier(selectedCompId);
        setActiveDossier(d);
      } catch (err) {
        console.error('Failed to fetch dossier:', err);
      }
    };
    loadDossier();
  }, [selectedCompId]);

  // 6. Load User Workspace when authenticated
  useEffect(() => {
    if (!session.authenticated) {
      setWorkspace(null);
      setAnnotation(null);
      return;
    }
    const loadWs = async () => {
      try {
        const ws = await fetchUserWorkspace();
        setWorkspace(ws);
        const curAnn = ws.annotations.find((a) => a.canonical_project_id === selectedCompId) || null;
        setAnnotation(curAnn);
      } catch (err) {
        console.error('Failed to load workspace:', err);
      }
    };
    loadWs();
  }, [session, selectedCompId]);

  // Handlers
  const handleSelectComp = (comp: TransactionComp) => {
    setSelectedCompId(comp.canonical_project_id);
    setIsDossierOpen(true);
  };

  const handleUnderwriteTarget = (target: QueueDistressRecord) => {
    setSelectedCompId(target.canonical_project_id);
    setSandboxPreset({
      it_load_mw: target.capacity_mw || 300,
      rent_rate_usd_kw_month: 180,
      user_notes: `Secondary queue acquisition underwrite for ${target.queue_request_id} (COD: ${target.current_study_cod}). Buyout range: $${target.estimated_buyout_min_usd?.toLocaleString()} - $${target.estimated_buyout_max_usd?.toLocaleString()}.`,
    });
    setCurrentTab('sandbox');
  };

  const handleUnderwriteArbitrage = (comp: TransactionComp, spread: number, multiple: number) => {
    setSelectedCompId(comp.canonical_project_id);
    const mw = comp.headline_capacity_mw || 250;
    setSandboxPreset({
      it_load_mw: mw,
      rent_rate_usd_kw_month: 185,
      capex_per_mw_usd: 8_500_000,
      debt_ltv_pct: 65,
      interest_rate_pct: 7.25,
      user_notes: `Queue-to-Deed Arbitrage Model for ${comp.deal_name}. Gross Value Spread: $${(spread / 1_000_000).toFixed(1)}M (${multiple}x Multiple).`,
    });
    setCurrentTab('sandbox');
  };

  const handleLoadInSandbox = (itMw: number, rentKwMo: number) => {
    setSandboxPreset({
      it_load_mw: itMw,
      rent_rate_usd_kw_month: rentKwMo,
    });
    setCurrentTab('sandbox');
  };

  const handleLoadScenarioIntoSandbox = (sc: UserSavedScenario) => {
    setSelectedCompId(sc.canonical_project_id);
    setSandboxPreset({
      it_load_mw: sc.it_load_mw,
      rent_rate_usd_kw_month: sc.rent_rate_usd_kw_month,
      capex_per_mw_usd: sc.capex_per_mw_usd,
      debt_ltv_pct: sc.debt_ltv_pct,
      interest_rate_pct: sc.interest_rate_pct,
      user_notes: sc.user_notes || '',
    });
    setCurrentTab('sandbox');
  };

  const handleSaveScenario = async (payload: {
    canonical_project_id: string;
    scenario_name: string;
    inputs: UnderwritingInputs;
  }) => {
    await saveUserScenario(payload);
    if (session.authenticated) {
      const ws = await fetchUserWorkspace();
      setWorkspace(ws);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    await deleteUserScenario(scenarioId);
    if (session.authenticated) {
      const ws = await fetchUserWorkspace();
      setWorkspace(ws);
    }
  };

  const handleSaveAnnotation = async (annData: Partial<UserAssetAnnotation>) => {
    await saveUserAnnotation({
      canonical_project_id: selectedCompId,
      deal_stage: annData.deal_stage || 'WATCHLIST',
      internal_rating: annData.internal_rating || 'TIER_1',
      private_notes: annData.private_notes || '',
      tags: annData.tags || [],
      is_starred: annData.is_starred || false,
    });
    if (session.authenticated) {
      const ws = await fetchUserWorkspace();
      setWorkspace(ws);
      const curAnn = ws.annotations.find((a) => a.canonical_project_id === selectedCompId) || null;
      setAnnotation(curAnn);
    }
  };

  const handleExportIcMemo = async (
    canonicalId: string,
    scenario?: UserSavedScenario | string | null
  ) => {
    let d = activeDossier;
    if (!d || d.canonical_project_id !== canonicalId) {
      try {
        d = await fetchDossier(canonicalId);
      } catch (e) {
        console.error('Failed to fetch dossier for IC memo export:', e);
      }
    }
    if (!d) return;

    let scObj: UserSavedScenario | null = null;
    if (scenario && typeof scenario === 'object') {
      scObj = scenario;
    } else if (typeof scenario === 'string') {
      scObj = workspace?.scenarios.find((s) => s.scenario_id === scenario) || null;
    }
    generateInstitutionalIcMemoPdf(d, scObj);
  };

  const handleOpenIcMemoModal = (canonicalId: string, scenario?: UserSavedScenario) => {
    setSelectedCompId(canonicalId);
    setActiveMemoScenario(scenario || null);
    setIsIcMemoModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[#06090e] text-[#E2E8F0]">
      {/* 0. Invisible Cross-Origin Ghost Member SSO Probe */}
      <GhostAuthProbe active={!session.authenticated} refreshKey={authProbeKey} />

      {/* 1. Flagship Header (Matches Map & Capital) */}
      <Header
        currentTab={currentTab}
        onSelectTab={setCurrentTab}
        session={session}
        onOpenAuthModal={() => setAuthModalOpen(true)}
        onOpenShareModal={() => setShareModalOpen(true)}
        savedScenariosCount={workspace?.scenarios.length || 0}
      />

      {/* 2. Key Metrics Telemetry Ribbon */}
      <MetricRibbon
        totalVolumeUsd={totalVolume}
        avgPricePerAcre={avgAcre}
        avgPricePerMw={avgMw}
        radarTargetsCount={radarTargets.length}
        compsCount={comps.length}
      />

      {/* 3. Main Workspace Viewport */}
      <main className="flex-1 overflow-hidden relative">
        {currentTab === 'comps' && (
          <TransactionsLedger
            comps={comps}
            selectedCompId={selectedCompId}
            onSelectComp={handleSelectComp}
            tier={session.tier}
            search={search}
            onSearchChange={setSearch}
            selectedState={selectedState}
            onStateChange={setSelectedState}
            selectedDealType={selectedDealType}
            onDealTypeChange={setSelectedDealType}
            selectedMinMw={selectedMinMw}
            onMinMwChange={setSelectedMinMw}
            onOpenAuthModal={() => setAuthModalOpen(true)}
          />
        )}

        {currentTab === 'arbitrage' && (
          <ArbitrageScreener
            comps={comps}
            tier={session.tier}
            onUnderwriteArbitrage={handleUnderwriteArbitrage}
            onSelectComp={handleSelectComp}
            onOpenAuthModal={() => setAuthModalOpen(true)}
          />
        )}

        {currentTab === 'radar' && (
          <DistressedQueueRadar
            radarTargets={radarTargets}
            tier={session.tier}
            onUnderwriteTarget={handleUnderwriteTarget}
            onOpenAuthModal={() => setAuthModalOpen(true)}
          />
        )}

        {currentTab === 'sandbox' && (
          <UnderwritingSandbox
            initialInputs={sandboxPreset}
            canonicalProjectId={selectedCompId}
            projectName={activeDossier?.canonical_name || selectedCompId}
            session={session}
            onSaveScenario={handleSaveScenario}
            onExportIcMemo={handleExportIcMemo}
            onOpenAuthModal={() => setAuthModalOpen(true)}
          />
        )}

        {currentTab === 'workspace' && (
          <UserWorkspaceView
            workspace={workspace}
            session={session}
            onLoadScenarioIntoSandbox={handleLoadScenarioIntoSandbox}
            onDeleteScenario={handleDeleteScenario}
            onExportIcMemo={handleExportIcMemo}
            onOpenIcMemoModal={handleOpenIcMemoModal}
            onOpenAuthModal={() => setAuthModalOpen(true)}
            onSelectCompById={(id) => {
              setSelectedCompId(id);
              setCurrentTab('comps');
              setIsDossierOpen(true);
            }}
          />
        )}

        {/* 4. Slide-Over Asset Dossier & Source Evidence Drawer */}
        <DossierDrawer
          isOpen={isDossierOpen}
          onClose={() => setIsDossierOpen(false)}
          dossier={activeDossier}
          session={session}
          annotation={annotation}
          onSaveAnnotation={handleSaveAnnotation}
          onLoadInSandbox={handleLoadInSandbox}
          onExportIcMemo={handleExportIcMemo}
          onOpenAuthModal={() => setAuthModalOpen(true)}
        />

        {/* 5. Investment Committee Deal Memo Pack Modal */}
        <IcMemoModal
          isOpen={isIcMemoModalOpen}
          onClose={() => setIsIcMemoModalOpen(false)}
          dossier={activeDossier}
          scenario={activeMemoScenario}
          session={session}
        />
      </main>

      {/* 6. Ghost SSO Authentication & Dev Tier Switcher Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        session={session}
        onUpdateSession={setSession}
      />

      {/* 7. Institutional Viral Share Modal */}
      <ViralShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        activeTab={currentTab}
        selectedAssetId={selectedCompId}
      />
    </div>
  );
};
